/**
 * Tab definitions + URL sync for the account detail view.
 *
 * The ops2026 shell already owns a top-level route in the URL
 * (`?view=account-detail&account=<id>`); we add a `?tab=<id>` param
 * that is scoped to this view only. Switching tabs rewrites the URL
 * via `history.replaceState` so:
 *
 *   - Reload keeps the current tab.
 *   - Links copied from the address bar deep-link to a tab.
 *   - The browser Back button still returns to the previous page
 *     (the accounts list, usually) rather than cycling tabs — that
 *     matched the view-router's existing behaviour and avoids
 *     fighting with its pushState history entries.
 *
 * Unknown tab ids fall back to the default (`overview`). The default
 * tab is also what we hand to new deep-links that omit `?tab=`.
 */

import { useEffect, useState } from 'react';

export type AccountTabId =
  | 'overview'
  | 'access'
  | 'sites'
  | 'billing'
  | 'features'
  | 'test'
  | 'danger';

export interface AccountTabDef {
  readonly id: AccountTabId;
  /** Button label (sentence case). */
  readonly label: string;
  /** Short description surfaced via aria-describedby / tooltip. Kept
   *  terse — the tab button itself is the primary signal. */
  readonly hint: string;
}

/**
 * Ordered left-to-right in the tab bar. The order mirrors how an
 * ops person reads an account: identity first → the people who
 * touch it → their sites → money → feature toggles → destructive
 * actions last.
 */
export const ACCOUNT_TABS: readonly AccountTabDef[] = [
  { id: 'overview', label: 'Overview', hint: 'Identity + status' },
  { id: 'access', label: 'Users & access', hint: 'Team roster, sessions, support login' },
  { id: 'sites', label: 'Sites', hint: 'Domains + regions' },
  { id: 'billing', label: 'Plan & billing', hint: 'Subscription, usage, entitlements' },
  { id: 'features', label: 'Features', hint: 'Feature toggles' },
  { id: 'test', label: 'Test', hint: 'Smoke test + UCP checkout test' },
  { id: 'danger', label: 'Danger zone', hint: 'Suspend, delete' },
] as const;

export const DEFAULT_ACCOUNT_TAB: AccountTabId = 'overview';

export function isAccountTabId(v: unknown): v is AccountTabId {
  return (
    v === 'overview'
    || v === 'access'
    || v === 'sites'
    || v === 'billing'
    || v === 'features'
    || v === 'test'
    || v === 'danger'
  );
}

/** Pure URL → tab-id resolver. Falls back to the default when the
 *  param is missing or unknown. Exported for the tab-routing tests. */
export function tabFromSearch(search: string): AccountTabId {
  try {
    const q = new URLSearchParams(search);
    const raw = q.get('tab');
    return isAccountTabId(raw) ? raw : DEFAULT_ACCOUNT_TAB;
  } catch {
    return DEFAULT_ACCOUNT_TAB;
  }
}

/** Pure URL writer: given a current URL + a tab, return the pathname
 *  + search + hash with `?tab=<id>` set. Exported for tests. */
export function urlWithTab(url: URL, tab: AccountTabId): string {
  const next = new URL(url.toString());
  if (tab === DEFAULT_ACCOUNT_TAB) {
    // Keep deep-link URLs tidy: the default tab doesn't need to be
    // spelled in the query string. A missing `tab=` reads as `overview`.
    next.searchParams.delete('tab');
  } else {
    next.searchParams.set('tab', tab);
  }
  return next.pathname + next.search + next.hash;
}

/**
 * React hook: read the current tab from the URL + a setter that
 * updates both the URL and local state.
 *
 * Uses `replaceState` so a tab switch doesn't push a new history
 * entry — the view-router already pushes one for `view=account-detail`
 * and we don't want to shadow its state with a tab-change entry that
 * popstate wouldn't recognise.
 */
export function useAccountTab(): {
  tab: AccountTabId;
  setTab: (next: AccountTabId) => void;
} {
  const [tab, setTabState] = useState<AccountTabId>(() => {
    if (typeof window === 'undefined') return DEFAULT_ACCOUNT_TAB;
    return tabFromSearch(window.location.search);
  });

  // If the user navigates (Back/Forward), re-read the URL and sync.
  useEffect(() => {
    if (typeof window === 'undefined') return;
    const onPop = (): void => {
      setTabState(tabFromSearch(window.location.search));
    };
    window.addEventListener('popstate', onPop);
    return () => window.removeEventListener('popstate', onPop);
  }, []);

  const setTab = (next: AccountTabId): void => {
    setTabState(next);
    if (typeof window === 'undefined') return;
    const current = window.location;
    const newUrl = urlWithTab(new URL(current.href), next);
    // Preserve the view-router's history state (`sill_ops_route`) so
    // Back still restores the previous page cleanly.
    const existing = window.history.state as Record<string, unknown> | null;
    window.history.replaceState(existing ?? {}, '', newUrl);
  };

  return { tab, setTab };
}
